/* eslint-disable @typescript-eslint/no-explicit-any */
import { ZodEffects, ZodNullable, ZodObject, ZodOptional, ZodRawShape, ZodType, ZodTypeAny, z } from 'zod';
import {
  CounselorProfileCertificationsSchema,
  CounselorProfilePersonalDetailsSchema,
  CounselorProfileSchoolDetailsSchema,
  CounselorProfileSchoolProfileSchema,
  RecommenderExtendedProfile,
  ClassRankReporting,
  CounselorFormStatus,
  TeacherRecommendationStatus,
  FormActionsStatus,
  FormNames,
  TeacherEvaluation,
  ApplicantColleges,
  SubjectArea,
  SchoolProfileSchema,
} from '@youscience/commonapp-service-common';
import { ReactElement } from 'react';
import { get, set, isEmpty, isEqual } from 'lodash-es';

import { FormState, FormStateErrors, OptionalNullableDateString } from '@constants/commonApp/types';

import { notify, NotifyOptions } from '@store/NotificationStore';

import FormRequired from '@images/Recommender/form-required.svg?react';
import FormOptional from '@images/Recommender/form-optional.svg?react';
import FormSubmitted from '@images/Recommender/form-submitted.svg?react';
import FormInProgress from '@images/Recommender/form-in-progress.svg?react';

import { FormMetadata, MemberCounselorForm, MemberForm } from './interfaces';
import { FormAvailabilityStatus, FormCompletionStatus, FormRequiredStatus } from './enums';
import { dateValidations } from '@utils/date/dateUtils';

interface GetNewStateProps<
  T,
  V extends
    | ZodEffects<ZodTypeAny>
    | ZodOptional<ZodTypeAny>
    | ZodObject<any>
    | ZodNullable<ZodOptional<ZodTypeAny>>
    | void = void,
> {
  data: T;
  validator?: V;
  prevState?: Partial<FormState<T>>;
  initialValues?: T;
}

export type ValidatorType =
  | ZodObject<any>
  | ZodOptional<ZodObject<any> | ZodEffects<any>>
  | ZodEffects<ZodTypeAny | ZodOptional<ZodTypeAny> | ZodObject<any>>
  | ZodNullable<ZodOptional<ZodTypeAny>>
  | void;

export const getNewFormState = <T, V extends ValidatorType = void>(props: GetNewStateProps<T, V>): FormState<T> => {
  const { data, validator, prevState, initialValues } = props;

  const newFormState = {
    ...prevState,
    data: {
      ...prevState?.data,
      ...data,
    },
    errors: {} as Partial<FormStateErrors<T>>,
    hasChanged: false,
    isSaved: prevState?.isSaved ?? false,
    isFormValid: prevState?.isFormValid ?? true,
  };

  if (initialValues) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const fields = Object.keys(newFormState.data!) as Array<keyof T>;
    const changedFields = fields.filter((field) => !isEqual(newFormState.data[field], initialValues[field]));
    newFormState.hasChanged = changedFields.length > 0;
  }

  if (validator && newFormState.isSaved) {
    newFormState.errors = validateSection(newFormState.data, validator) as FormStateErrors<T>;
  }

  if (validator) newFormState.isFormValid = Object.values(newFormState.errors).filter((value) => value).length === 0;

  return newFormState;
};

const validateSection = <
  T,
  V extends ZodEffects<ZodTypeAny> | ZodOptional<ZodTypeAny> | ZodObject<any> | ZodNullable<any> | void = void,
>(
  data: T,
  validator?: V,
): Record<string, string> => {
  if (!validator) {
    return {};
  }

  const errors = {} as Record<string, string>;
  const issues = validator.safeParse(data).error?.issues;

  if (issues) {
    issues.forEach((issue) => {
      errors[issue.path[0]] = issue.message;
    });
  }

  return errors;
};

export const checkFormValidity = <
  V extends ZodObject<any> | ZodEffects<ZodObject<ZodRawShape>> | ZodNullable<ZodTypeAny>,
>(
  form: { [key: string]: { [key: string]: string | undefined } | any | undefined | null } | null | undefined,
  validator: V,
): boolean => {
  const errors = validator.safeParse(form).error?.issues;
  if (errors?.length && errors.length > 0) {
    return false;
  }

  return true;
};

type ZObjectType = ZodType<Record<string | number, unknown> | undefined>;
export const handleZodValidation = <T extends ZObjectType | ZodNullable<ZodTypeAny>>(
  inputName: string,
  schema: T,
  data: Partial<T['_output']>,
) => {
  const validation = schema.safeParse(data);
  const error = validation.error?.issues.find((issue) => issue.path.includes(inputName));

  return {
    error,
    isError: !!error,
    isSuccess: validation.success,
  };
};

export const checkCounselorProfileValidity = (
  counselorProfile: RecommenderExtendedProfile,
): { personal: boolean; school: boolean } => {
  const personalDetailsError =
    checkFormValidity(counselorProfile?.counselorProfile, CounselorProfilePersonalDetailsSchema) &&
    checkFormValidity(counselorProfile?.counselorProfile?.certifications, CounselorProfileCertificationsSchema);

  const schoolDetailsError = checkFormValidity(
    { highSchoolCeebCode: counselorProfile?.highSchool?.schoolCode, ...counselorProfile?.schoolProfile?.schoolDetails },
    CounselorProfileSchoolDetailsSchema,
  );

  const schoolProfile = coerceStringFieldsForSchoolProfile({
    ...counselorProfile?.schoolProfile,
    ...counselorProfile?.schoolProfile?.schoolProfile,
    schoolProfile: counselorProfile?.schoolProfile?.schoolProfile?.schoolProfile,
  });

  const classRank = counselorProfile?.schoolProfile?.classRank;
  const gpa = counselorProfile?.schoolProfile?.gpa;

  const schoolProfileError = checkFormValidity(schoolProfile, CounselorProfileSchoolProfileSchema);
  const isErrorClassRank =
    (!classRank?.reporting || classRank?.reporting !== ClassRankReporting.None) &&
    (!classRank?.startDate || !classRank?.endDate || !classRank?.weighting);
  const isErrorGpa =
    (!gpa?.reporting || gpa?.reporting === '0') &&
    (!gpa?.scale || !gpa?.startDate || !gpa?.endDate || !gpa?.weighting || !gpa?.passing || !gpa?.highest);
  const isErrorTranscripts =
    !counselorProfile?.schoolProfile?.transcripts?.testsIncluded ||
    !counselorProfile?.schoolProfile?.transcripts?.currentCoursesListed;

  return {
    school: schoolDetailsError && schoolProfileError && !isErrorClassRank && !isErrorGpa && !isErrorTranscripts,
    personal: personalDetailsError,
  };
};

const coerceStringFieldsForSchoolProfile = (
  schoolProfile: Partial<z.infer<typeof SchoolProfileSchema>>,
): Partial<z.infer<typeof SchoolProfileSchema>> => {
  schoolProfile.classSize = formatString(schoolProfile.classSize || undefined) as string | undefined;

  if (schoolProfile?.percentages) {
    for (const percentage of Object.keys(schoolProfile.percentages) as Array<keyof typeof schoolProfile.percentages>) {
      schoolProfile.percentages[percentage] = formatString(schoolProfile.percentages[percentage]);
    }
  }

  const courseNames = ['ap', 'ib', 'honors', 'cambridge'] as const;
  courseNames.forEach((name) => {
    const course = schoolProfile.courses?.[name];
    if (course) {
      if (course.limit !== null && course.limit !== undefined) {
        course.limit = formatString(course.limit);
      }

      if (course.offering !== null && course.offering !== undefined) {
        course.offering = formatString(course.offering);
      }
    }
  });

  return schoolProfile;
};

const formatString = (value: unknown): string | undefined | null => {
  return value !== null && value !== undefined ? String(value) : value;
};

export const getFilename = (applicantName: string, field: string) => {
  return `${applicantName}_${field}.pdf`.replace(/\s+/g, '_');
};

export const setAllSectionsToValue = <T extends Record<string, unknown>>(
  sections: T,
  value: boolean,
): Record<keyof T, boolean> => {
  return Object.keys(sections).reduce((acc, section) => ({ ...acc, [section]: value }), {} as Record<keyof T, boolean>);
};

export const UPDATE_ERROR_NOTIFICATION = {
  message: 'Looks like something went wrong. Please try again later.',
  severity: 'error',
} as NotifyOptions;

export const PREVIEW_ERROR_NOTIFICATION = {
  message: 'There was an error generating the preview. Please try again later.',
  severity: 'error',
} as NotifyOptions;

export const VALIDATION_ERROR_NOTIFICATION = {
  message: 'Please complete all required fields before preview.',
  severity: 'error',
} as NotifyOptions;

export const REVIEW_NOT_READY_ERROR_NOTIFICATION = {
  message: 'Your request is being processed. Please check back in a few minutes.',
  severity: 'info',
} as NotifyOptions;

const orderedStatuses = {
  [FormCompletionStatus.NEW]: 0,
  [FormCompletionStatus.IN_PROGRESS]: 1,
  [FormCompletionStatus.PENDING]: 3,
  [FormCompletionStatus.REJECTED]: 3,
  [FormCompletionStatus.SUBMITTED]: 4,
  [FormCompletionStatus.DOWNLOADED]: 5,
};

const availabilityRules = {
  [FormNames.SECONDARY_REPORT]: {
    enablers: [],
    disablers: [FormNames.OPTIONAL_REPORT, FormNames.MIDYEAR_REPORT, FormNames.FINAL_REPORT],
  },
  [FormNames.OPTIONAL_REPORT]: {
    enablers: [FormNames.SECONDARY_REPORT],
    disablers: [FormNames.OPTIONAL_REPORT2, FormNames.FINAL_REPORT],
  },
  [FormNames.OPTIONAL_REPORT2]: {
    enablers: [FormNames.SECONDARY_REPORT, FormNames.OPTIONAL_REPORT],
    disablers: [FormNames.FINAL_REPORT],
  },
  [FormNames.MIDYEAR_REPORT]: { enablers: [FormNames.SECONDARY_REPORT], disablers: [FormNames.FINAL_REPORT] },
  [FormNames.FINAL_REPORT]: { enablers: [FormNames.SECONDARY_REPORT], disablers: [] },
  [FormNames.RECOMMENDATION]: { enablers: [], disablers: [FormNames.FINAL_REPORT] },
  [FormNames.TEACHER_EVALUATION]: { enablers: [], disablers: [] },
  [FormNames.EARLY_DECISION]: { enablers: [], disablers: [] },
  [FormNames.EARLY_DECISION2]: { enablers: [], disablers: [] },
  [FormNames.FEE_WAIVER]: { enablers: [], disablers: [] },
} as Record<FormNames, { enablers: FormNames[]; disablers: FormNames[] }>;

const getEarliestDate = (
  date1: OptionalNullableDateString | Date,
  date2: OptionalNullableDateString | Date,
): OptionalNullableDateString | Date => {
  if (!date1 && !date2) return null;

  if (!date1) return new Date(date2!);
  if (!date2) return new Date(date1);

  return new Date(date1) < new Date(date2) ? new Date(date1) : new Date(date2);
};

const mergeFormMetadata = (form: FormMetadata | undefined, newForm: MemberForm): FormMetadata => {
  const mergedForm = { ...(form || {}) } as FormMetadata;

  if (mergedForm.requiredStatus !== FormRequiredStatus.REQUIRED) {
    if (newForm.status === CounselorFormStatus.REQUIRED || mergedForm?.name === FormNames.TEACHER_EVALUATION) {
      mergedForm.requiredStatus = FormRequiredStatus.REQUIRED;
    } else if (newForm.status === CounselorFormStatus.NOT_REQUIRED) {
      mergedForm.requiredStatus = FormRequiredStatus.OPTIONAL;
    } else {
      mergedForm.requiredStatus = FormRequiredStatus.NOT_APPLICABLE;
    }
  }

  if (mergedForm.completionStatus !== FormCompletionStatus.DOWNLOADED) {
    if (newForm.downloadedDate) {
      mergedForm.completionStatus = FormCompletionStatus.DOWNLOADED;
    } else if (newForm.submittedDate) {
      mergedForm.completionStatus = FormCompletionStatus.SUBMITTED;
    } else if (mergedForm.completionStatus !== FormCompletionStatus.SUBMITTED) {
      if (newForm.status === TeacherRecommendationStatus.REJECTED) {
        mergedForm.completionStatus = FormCompletionStatus.REJECTED;
      } else if (newForm.status === TeacherRecommendationStatus.PENDING) {
        mergedForm.completionStatus = FormCompletionStatus.PENDING;
      } else if (
        newForm.status === TeacherRecommendationStatus.IN_PROGRESS ||
        (newForm as MemberCounselorForm).formActionStatus === FormActionsStatus.IN_PROGRESS
      ) {
        mergedForm.completionStatus = FormCompletionStatus.IN_PROGRESS;
      } else {
        mergedForm.completionStatus = FormCompletionStatus.NEW;
      }
    }
  }

  mergedForm.submittedDate = getEarliestDate(mergedForm.submittedDate, newForm.submittedDate);
  mergedForm.downloadedDate = getEarliestDate(mergedForm.downloadedDate, newForm.downloadedDate);

  return mergedForm;
};

const getFormsAvailabilityStatus = (
  formsMetadata: Record<FormNames, FormMetadata>,
): Record<FormNames, FormAvailabilityStatus> => {
  const formsCompletionStatus = Object.keys(formsMetadata).reduce((acc, formName) => {
    acc[formName as FormNames] = formsMetadata[formName as FormNames].completionStatus!;
    return acc;
  }, {} as Record<FormNames, FormCompletionStatus>);
  const formAvailabilityStatus = {} as Record<FormNames, FormAvailabilityStatus>;

  for (const formName of Object.keys(formsCompletionStatus) as FormNames[]) {
    formAvailabilityStatus[formName] = FormAvailabilityStatus.NOT_YET_AVAILABLE;
    const enablers = availabilityRules[formName].enablers;
    const disablers = availabilityRules[formName].disablers;

    if (
      enablers.every(
        (enabler) => orderedStatuses[formsCompletionStatus[enabler]] >= orderedStatuses[FormCompletionStatus.SUBMITTED],
      )
    ) {
      if (formsCompletionStatus[formName] === FormCompletionStatus.DOWNLOADED) {
        formAvailabilityStatus[formName] = FormAvailabilityStatus.NOT_AVAILABLE;
      } else if (
        disablers.some(
          (disabler) =>
            orderedStatuses[formsCompletionStatus[disabler]] >= orderedStatuses[FormCompletionStatus.SUBMITTED],
        )
      ) {
        formAvailabilityStatus[formName] = FormAvailabilityStatus.NOT_AVAILABLE;
      } else {
        formAvailabilityStatus[formName] = FormAvailabilityStatus.AVAILABLE;
      }
    } else if (
      disablers.some(
        (disabler) =>
          orderedStatuses[formsCompletionStatus[disabler]] >= orderedStatuses[FormCompletionStatus.SUBMITTED],
      )
    ) {
      formAvailabilityStatus[formName] = FormAvailabilityStatus.NOT_AVAILABLE;
    }
  }

  return formAvailabilityStatus;
};

export const isFormRequiredForMember = (formName: FormNames, member: ApplicantColleges['members'][number]): boolean => {
  if ([FormNames.OPTIONAL_REPORT, FormNames.OPTIONAL_REPORT2].includes(formName)) return false;

  if (formName === FormNames.RECOMMENDATION) return member.counselorEvalReqd ?? false;
  if (formName === FormNames.MIDYEAR_REPORT) return member.midYearRequired ?? false;

  return true;
};

export const getFormsMetadata = (applicant: ApplicantColleges): Record<FormNames, FormMetadata> => {
  const forms = {} as Record<FormNames, FormMetadata>;

  let earliestDeadline = null;

  for (const member of applicant.members) {
    if (member.deadline) {
      earliestDeadline = getEarliestDate(earliestDeadline, member.deadline);
    }

    if (member.counselor.recommenderId === applicant.counselorRecommenderId) {
      if (member.counselor.forms?.length) {
        for (const counselorForm of member.counselor.forms) {
          const newForm = { ...counselorForm };

          if (newForm.formActionStatus === FormActionsStatus.NEW && applicant.forms[newForm.type]) {
            newForm.formActionStatus = FormActionsStatus.IN_PROGRESS;
          }

          const members = forms[newForm.type as FormNames]?.members || [];

          members.push({
            memberId: member.memberId,
            memberName: member.collegeName ?? '',
            submittedDate: newForm.submittedDate,
            downloadedDate: newForm.downloadedDate,
            required: isFormRequiredForMember(newForm.type as FormNames, member),
          });

          forms[newForm.type as FormNames] = mergeFormMetadata(
            { ...forms[newForm.type as FormNames], name: newForm.type },
            newForm,
          );
          forms[newForm.type as FormNames].members = members;
        }
      }
    }

    const teacher = member.teachers.find((teacher) => teacher.recommenderId === applicant.teacherRecommenderId);

    if (teacher) {
      const newForm = {
        ...teacher.recommendation,
      };
      if (teacher.recommenderId === applicant.teacherRecommenderId) {
        if (
          newForm.status === TeacherRecommendationStatus.NOT_STARTED &&
          applicant.forms[FormNames.TEACHER_EVALUATION]?.some(
            (recommendation: TeacherEvaluation) => recommendation.recommenderId === teacher.recommenderId,
          )
        ) {
          newForm.status = TeacherRecommendationStatus.IN_PROGRESS;
        }

        const members = forms[FormNames.TEACHER_EVALUATION]?.members || [];

        members.push({
          memberId: member.memberId,
          memberName: member.collegeName ?? '',
          submittedDate: newForm.submittedDate,
          downloadedDate: newForm.downloadedDate,
          required: isFormRequiredForMember(FormNames.TEACHER_EVALUATION, member),
        });

        forms[FormNames.TEACHER_EVALUATION] = {
          ...mergeFormMetadata({ ...forms[FormNames.TEACHER_EVALUATION], name: FormNames.TEACHER_EVALUATION }, newForm),
          subjectArea: teacher.subject as SubjectArea,
          isTeacherCounselor: applicant.counselorRecommenderId === teacher.recommenderId,
        };

        forms[FormNames.TEACHER_EVALUATION].members = members;
      }
    }
  }

  for (const formName of Object.keys(forms) as FormNames[]) {
    forms[formName].deadline = earliestDeadline;
    forms[formName].studentName = applicant.legalName;
    forms[formName].applicantId = applicant.applicantId;
    forms[formName].recommenderId =
      formName === FormNames.TEACHER_EVALUATION ? applicant.teacherRecommenderId! : applicant.counselorRecommenderId!;
    forms[formName].name = formName;
  }

  const formsAvailabilityStatus = getFormsAvailabilityStatus(forms);

  for (const formName of Object.keys(formsAvailabilityStatus)) {
    forms[formName as FormNames].availabilityStatus = formsAvailabilityStatus[formName as FormNames];
  }

  return forms;
};

const isFormSubmitted = (form: FormMetadata) =>
  [FormCompletionStatus.SUBMITTED, FormCompletionStatus.DOWNLOADED].includes(
    form.completionStatus || FormCompletionStatus.NEW,
  );

const isFormInProgress = (form: FormMetadata) =>
  [FormCompletionStatus.IN_PROGRESS, FormCompletionStatus.PENDING, FormCompletionStatus.REJECTED].includes(
    form.completionStatus || FormCompletionStatus.NEW,
  );

const isFormNew = (form: FormMetadata) => form.completionStatus === FormCompletionStatus.NEW;

export const getFormStatusIcon = (form: FormMetadata, memberId?: number): ReactElement => {
  const isRequired = memberId
    ? form.members.find((m) => m.memberId === memberId)?.required
    : form.requiredStatus === FormRequiredStatus.REQUIRED;

  if (isFormSubmitted(form)) {
    return <FormSubmitted />;
  } else if (isFormInProgress(form)) {
    return <FormInProgress />;
  } else if (isRequired) {
    return <FormRequired />;
  } else {
    return <FormOptional />;
  }
};

export const getFormStatusText = (form: FormMetadata, memberId?: number): string => {
  const isSubmitted = isFormSubmitted(form);
  const isAvailable = form.availabilityStatus === FormAvailabilityStatus.AVAILABLE;

  const formStatus = !isAvailable
    ? isSubmitted
      ? form.completionStatus
      : form.availabilityStatus
    : isFormNew(form)
    ? form.availabilityStatus
    : form.completionStatus;

  if (memberId) {
    const requiredFormMember = form.members.find((m) => m.memberId === memberId)?.required;
    return [requiredFormMember ? FormRequiredStatus.REQUIRED : FormRequiredStatus.OPTIONAL, formStatus].join(' / ');
  }

  return [form.requiredStatus, formStatus].join(' / ');
};

export const getFormStatusTextAndIcon = (
  form: FormMetadata,
  memberId?: number,
): { text: string; icon: ReactElement | null; disabled: boolean } => {
  const isSubmitted = isFormSubmitted(form);
  const isAvailable = form.availabilityStatus === FormAvailabilityStatus.AVAILABLE;

  return {
    text: getFormStatusText(form, memberId),
    icon: getFormStatusIcon(form, memberId),
    disabled: !isAvailable && !isSubmitted,
  };
};

export const getGeneralStudentStatus = (forms: Record<FormNames, FormMetadata>): FormCompletionStatus => {
  const availableForms = Object.values(forms).filter(
    (form) => form.availabilityStatus === FormAvailabilityStatus.AVAILABLE,
  );

  const notAvailableForms = Object.values(forms).filter(
    (form) => form.availabilityStatus === FormAvailabilityStatus.NOT_AVAILABLE,
  );

  if (availableForms.every((form) => isFormNew(form)) && !notAvailableForms.some((form) => isFormSubmitted(form))) {
    return FormCompletionStatus.NEW;
  } else if (availableForms.every((form) => isFormSubmitted(form)) || availableForms.length === 0) {
    return FormCompletionStatus.SUBMITTED;
  } else {
    return FormCompletionStatus.IN_PROGRESS;
  }
};

export const countWordsForLongTextFields = (text?: string | null): number => {
  const adjustedText = text ?? '';
  return adjustedText.split(' ').filter(Boolean).length + (adjustedText.match(/(\r?\n)|\r/g)?.length || 0);
};

export const dateRangeValidation = <
  T extends { startDate?: OptionalNullableDateString; endDate?: OptionalNullableDateString },
>(
  formState: FormState<Partial<T>>,
  fieldUpdates: Partial<T>,
): FormStateErrors<T> => {
  let startDate = formState.data.startDate;
  let endDate = formState.data.endDate;

  if (Object.keys(fieldUpdates).includes('startDate')) {
    startDate = fieldUpdates.startDate;
  }

  if (Object.keys(fieldUpdates).includes('endDate')) {
    endDate = fieldUpdates.endDate;
  }

  return {
    startDate: undefined,
    endDate: undefined,
    ...dateValidations(
      [
        { label: 'startDate', value: startDate },
        { label: 'endDate', value: endDate },
      ],
      [startDate, endDate],
    ),
  };
};

export const onSaveSuccessNotification = (formName: FormNames) => {
  notify({
    message: `${formName} updates have been saved successfully.`,
    severity: 'success',
  });
};

export const onSubmitSuccessNotification = (formName: FormNames) => {
  notify({
    message: `${formName} has been submitted successfully.`,
    severity: 'success',
  });
};

export const onUnsubmitSuccessNotification = (formName: FormNames) => {
  notify({
    message: `${formName} has been unsubmitted successfully.`,
    severity: 'success',
  });
};

export const onSaveErrorNotification = () => {
  notify(UPDATE_ERROR_NOTIFICATION);
};
